import styled, { keyframes } from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../utils/constants";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Mirrors the real SavingsPage composition (same boxes, margins, radii, breakpoints) so the
// loading state and loaded page keep cumulative layout shift near zero.
export const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
`;

export const FiltersSkeleton = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.md}px;
  margin: 0 ${SPACING_RESPONSIVE.mobile.pageMargin}px;

  @media ${QUERIES.tabletAndUp} {
    margin: 0 ${SPACING_RESPONSIVE.tablet.pageMargin}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    justify-content: space-between;
    margin: 0 ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    padding: ${SPACING.md}px ${SPACING.lg}px;
    border-radius: ${SPACING.md}px;
    background: var(--color-background-2);
    border: 1px solid var(--color-border-light);
    box-shadow: var(--shadow-small);
  }
`;

export const FiltersGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.md}px;
`;

export const FiltersCount = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: block;
  }
`;

export const InsightsSkeleton = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
  }

  margin-bottom: 0;
`;

export const InsightsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-tab-background);

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const InsightsHeaderSpacer = styled.div`
  margin-left: auto;
`;

export const InsightsBody = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: grid;
    grid-template-columns: 1fr;
    gap: ${SPACING.xl}px;
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: 1fr 1fr;
    gap: ${SPACING.xxl}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const InsightsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const InsightsColumnHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const InsightsSkeletonList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const InsightsRow = styled.li`
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) 88px;
  align-items: center;
  gap: ${SPACING.md}px;
`;

export const HoldingsSkeleton = styled.section`
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    margin-top: 0;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    margin-top: 0;
  }
`;

export const HoldingsTable = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: block;
    overflow: hidden;
    border-radius: ${SPACING.md}px;
    background: var(--color-card-background);
    border: 1px solid var(--color-border-light);
    box-shadow: var(--shadow-small);
  }
`;

export const HoldingsTableHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.lg}px;
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  background: var(--color-tab-background);
  border-bottom: 1px solid var(--color-border-light);
`;

export const HoldingsTableRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.lg}px;
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border-light);
  }
`;

export const HoldingsTableFoot = styled.div`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  border-top: 1px solid var(--color-border-light);
  background: var(--color-tab-background);
`;

export const HoldingsNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  flex: 2 1 0;
  min-width: 0;
`;

export const HoldingsValueCell = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1 1 0;
`;

export const HoldingsTrendCell = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 0 0 120px;
`;

export const HoldingsCards = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const HoldingsCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
`;

export const HoldingsCardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const SkeletonBox = styled.div<{ width?: string; height?: string }>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "32px"};
  background: var(--gradient-skeleton);
  background-size: 200% 100%;
  animation: loading 1.5s ${ANIMATION.easing.easeInOut} infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
